import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  ViewChild,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { PaperIconComponent } from '@ricoh-paper/angular';
import { ADMIN_SECTIONS, AdminSection } from '../../../../core/services/admin-tabs.utils';
import { environment } from '../../../../../environments/environment';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { LocaleService } from '../../../../core/services/locale.service';

/**
 * RESPONSIBILITY: floating combobox-style picker for jumping to any admin
 *   section. Fuzzy-matches the user's query against label, description,
 *   url, and keywords. Emits `select(section)` on activation; `dismiss`
 *   when the user presses Esc or clicks outside.
 *
 * USED BY:
 *   - <im-admin-search-pill> (the ⌘P search affordance above the rail)
 *   - Eventually: the global ⌘K palette can register the same sections
 *     as navigation commands so power users have a single search.
 *
 * AI CONTEXT:
 *   - Visible items are `ADMIN_SECTIONS` minus the devOnly entries when
 *     the build is production.
 *   - Keyboard: ↑/↓ moves the cursor, Enter activates, Esc dismisses.
 *   - The picker is presentation-only — caller owns the open/close state
 *     and decides how to handle the selected section (current tab, new
 *     tab, etc.).
 */
@Component({
  selector: 'im-admin-search-picker',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, PaperIconComponent,
    TranslocoPipe,
  ],
  templateUrl: './admin-search-picker.component.html',
  styleUrl: './admin-search-picker.component.scss',
})
export class AdminSearchPickerComponent {
  /** Viewport coordinates the picker should anchor against. `right` is
   *  measured from the viewport's right edge so the popover right-aligns
   *  under its trigger and never spills off-screen. */
  readonly anchor = input<{ top: number; right: number } | null>(null);

  readonly select = output<AdminSection>();
  readonly dismiss = output<void>();

  protected readonly query = signal<string>('');
  protected readonly cursor = signal<number>(0);

  @ViewChild('queryInput') protected queryInput?: ElementRef<HTMLInputElement>;

  private readonly availableSections = computed<AdminSection[]>(() =>
    ADMIN_SECTIONS.filter(s => !s.devOnly || !environment.production),
  );

  private readonly transloco = inject(TranslocoService);
  private readonly locale = inject(LocaleService);

  protected readonly results = computed<AdminSection[]>(() => {
    this.locale.locale();
    const q = this.query().trim().toLowerCase();
    const all = this.availableSections();
    if (!q) return all;
    return all.filter(s => {
      const haystack = [
        s.label,
        s.labelKey ? this.transloco.translate(s.labelKey) : '',
        s.description ?? '',
        s.descriptionKey ? this.transloco.translate(s.descriptionKey) : '',
        s.url,
        s.group ?? '',
        s.groupKey ? this.transloco.translate(s.groupKey) : '',
        ...(s.keywords ?? []),
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  });

  /** Group results into eyebrow buckets — keeps the list scannable when
   *  there are no query filters. When the user is searching, hide the
   *  bucket headings to make the result count obvious. */
  protected readonly grouped = computed<readonly { headingKey: string | null; items: readonly AdminSection[] }[]>(() => {
    const list = this.results();
    if (this.query().trim().length > 0) {
      return [{ headingKey: null, items: list }];
    }
    const buckets = new Map<string, AdminSection[]>();
    const order: string[] = [];
    for (const s of list) {
      const key = s.groupKey ?? '';
      let bucket = buckets.get(key);
      if (!bucket) {
        bucket = [];
        buckets.set(key, bucket);
        order.push(key);
      }
      bucket.push(s);
    }
    return order.map(key => ({
      headingKey: key === '' ? null : key,
      items: buckets.get(key) ?? [],
    }));
  });

  ngOnInit(): void {
    queueMicrotask(() => this.queryInput?.nativeElement.focus());
  }

  @HostListener('document:click', ['$event'])
  protected onDocClick(event: MouseEvent): void {
    const host = (this.queryInput?.nativeElement?.closest('.picker') as HTMLElement | null);
    if (host && !host.contains(event.target as Node)) {
      this.dismiss.emit();
    }
  }

  protected onKeydown(event: KeyboardEvent): void {
    const flat = this.results();
    const max = flat.length - 1;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (max >= 0) this.cursor.set(Math.min(this.cursor() + 1, max));
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (max >= 0) this.cursor.set(Math.max(this.cursor() - 1, 0));
        break;
      case 'Enter': {
        event.preventDefault();
        const pick = this.results()[this.cursor()];
        if (pick) this.select.emit(pick);
        break;
      }
      case 'Escape':
        event.preventDefault();
        this.dismiss.emit();
        break;
    }
  }

  protected onQueryChange(value: string): void {
    this.query.set(value);
    this.cursor.set(0);
  }

  protected onMouseEnter(idx: number): void {
    this.cursor.set(idx);
  }

  protected onClick(section: AdminSection): void {
    this.select.emit(section);
  }
}
